<template>
  <section id="products" class="section products" aria-labelledby="products-title">
    <div class="container">
      <h2 id="products-title" class="title">{{ t('products.title') }}</h2>
      <p class="subtitle">{{ t('products.subtitle') }}</p>

      <div class="scroller" @mouseenter="hovering = true" @mouseleave="hovering = false">
        <div class="track" :class="{ paused: hovering }">
          <RouterLink
            v-for="(p, idx) in looped"
            :key="p.id + '-' + idx"
            class="card" :to="'/products/' + p.id" :style="styleFor(idx)"
          >
            <div class="thumb" aria-hidden="true">
              <img class="img" :src="p.image" :alt="pTitle(p)" loading="lazy" />
            </div>
            <h3 class="card-title">{{ pTitle(p) }}</h3>
            <p class="card-desc">{{ pDesc(p) }}</p>
          </RouterLink>
        </div>
      </div>

      <div class="cta-wrap">
        <RouterLink class="view-all" to="/products">{{ t('products.viewAll') }}</RouterLink>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { products } from '../data/products'

const { t, locale } = useI18n()
const hovering = ref(false)

const looped = computed(() => {
  // Duplicate list to create seamless scroll
  return [...products, ...products]
})

const hues = [200, 215, 225, 210, 230]
const styleFor = (i) => ({ '--h': hues[i % hues.length] })
const pTitle = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.title || p.en.title
const pDesc = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.desc || p.en.desc
</script>

<style scoped>
.section.products {
  position: relative; z-index: 0; overflow: hidden;
  padding: 5rem 1.5rem 6rem;
  background: radial-gradient(1200px 600px at 10% -20%, rgba(96,165,250,0.12), transparent),
              radial-gradient(900px 500px at 110% 120%, rgba(37,99,235,0.14), transparent),
              linear-gradient(180deg, rgba(6,10,22,1) 0%, rgba(10,14,28,1) 100%);
}
.section.products::before {
  content: '';
  position: absolute; inset: -30%; z-index: -1; pointer-events: none;
  background: radial-gradient(40% 50% at 15% 20%, rgba(96,165,250,0.35), transparent 60%),
              radial-gradient(50% 55% at 85% 30%, rgba(37,99,235,0.32), transparent 65%),
              radial-gradient(45% 50% at 50% 90%, rgba(59,130,246,0.25), transparent 65%);
  animation: aurora 18s ease-in-out infinite alternate;
  mix-blend-mode: screen;
}
.container { max-width: 1100px; margin: 0 auto; }
.title { color: #e6f0ff; font-size: clamp(1.6rem, 4vw, 2.4rem); text-align: center; }
.subtitle { color: #9fb0ff; text-align: center; margin-bottom: 1.25rem; }

/* Horizontal auto-scroller */
.scroller { position: relative; overflow: hidden; mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
.track {
  display: flex; gap: 1rem; align-items: stretch;
  width: max-content;
  animation: roll 30s linear infinite;
}
.track.paused { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) {
  .track { animation: none; }
  .section.products::before { animation: none; }
}

.card {
  --h: 210;
  flex: 0 0 auto;
  width: 380px; max-width: 85vw;
  position: relative;
  padding: 1.3rem;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(18,22,40,0.9), rgba(14,18,34,0.9));
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(255,255,255,0.02);
  transition: transform .25s ease, box-shadow .25s ease;
  overflow: hidden;
  text-decoration: none;
}
.card::before {
  content: '';
  position: absolute; inset: -1px;
  background: conic-gradient(from 0deg, hsla(var(--h),90%,60%,.0), hsla(var(--h),90%,60%,.35), hsla(var(--h),90%,60%,.0));
  filter: blur(16px);
  opacity: .35;
  transform: rotate(0deg);
  animation: spin 10s linear infinite;
}
.card:hover { transform: translateY(-4px) scale(1.02); box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
.card-title { color: #ebf3ff; margin: 0 0 .3rem; font-size: 1.25rem; }
.card-desc { color: #b7c5ff; margin: 0; font-size: 1.05rem; }

/* View all button */
.cta-wrap { display: flex; justify-content: center; margin-top: 1.25rem; }
.view-all {
  display: inline-block; padding: .7rem 1.1rem; border-radius: 12px;
  background: linear-gradient(135deg,#60a5fa,#2563eb); color: #fff; text-decoration: none;
  box-shadow: 0 8px 22px rgba(59, 130, 246, .30);
}
.view-all:hover { filter: brightness(1.05); }

/* Download action */
.actions { display: flex; justify-content: flex-end; margin-top: .6rem; }
.download {
  display: inline-block; padding: .35rem .6rem; border-radius: 10px; text-decoration: none;
  background: rgba(255,255,255,0.08); color: #dbe7ff; border: 1px solid rgba(255,255,255,0.12);
  font-size: .85rem;
}
.download:hover { background: linear-gradient(135deg,#60a5fa,#2563eb); color: white; border-color: transparent; }

@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
@keyframes roll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@keyframes aurora {
  0% { transform: translate3d(-2%, -1%, 0) scale(1.05); filter: hue-rotate(0deg) saturate(1.2); }
  50% { transform: translate3d(1%, 2%, 0) scale(1.08); filter: hue-rotate(25deg) saturate(1.25); }
  100% { transform: translate3d(-1%, 1%, 0) scale(1.03); filter: hue-rotate(-15deg) saturate(1.2); }
}
</style>


/* Thumbnail image */
.thumb { position: relative; border-radius: 12px; overflow: hidden; aspect-ratio: 16 / 9; background: rgba(255,255,255,0.04); margin-bottom: .6rem; }
.img { width: 100%; height: 100%; object-fit: cover; display: block; filter: saturate(1.2) contrast(1.05); }
.thumb::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.15) 100%); }
